import { useEffect, useCallback, useMemo, useRef } from 'react';
import dayjs from 'dayjs';
import { useAppDispatch } from '../../../store/store';
import {
  BlacklistRequest,
  getBlacklist,
} from '../../../store/reducers/BlacklistSlice';
import { BlacklistTablet } from '../../../models/Blacklist/Blacklist';
import { useDebounce } from '../../shared/useDebounce';
import { createServerSortChangeHandler } from '../../shared/useServerTableSort';
import {
  blacklistServerSortDefaults,
  blacklistSortMap,
} from '../../shared/journalSortMaps';
import { useBlacklistColumns } from './useBlacklistColumns/useBlacklistColumns';
import { useBlacklistHandlers } from './useBlacklistHandlers/useBlacklistHandlers';
import { useBlacklistState } from './useBlacklistState/useBlacklistState';
import type { ResettableRangePickerValue } from '../../../components/shared/ResettableRangePicker/ResettableRangePicker';

export const useBlacklist = () => {
  const {
    setIndex,
    pageSize,
    setPageSize,
    callRequest,
    setCallRequest,
    total,
    currentBlacklist,
    allBlacklist,
    status,
  } = useBlacklistState();

  const dispatch = useAppDispatch();

  const onTableChange = useMemo(
    () =>
      createServerSortChangeHandler<BlacklistTablet, BlacklistRequest>(
        callRequest,
        blacklistSortMap,
        setCallRequest,
        blacklistServerSortDefaults,
      ),
    [callRequest, setCallRequest],
  );

  const debouncedTextSearch = useDebounce(callRequest.TextSearch, 500);
  const debouncedReason = useDebounce(callRequest.Reason, 500);

  const dateRange = useMemo<ResettableRangePickerValue>(() => {
    if (!callRequest.From || !callRequest.To) {
      return null;
    }

    return [dayjs(callRequest.From), dayjs(callRequest.To)];
  }, [callRequest.From, callRequest.To]);

  const prevTextSearchRef = useRef<string>('');

  useEffect(() => {
    const isTextSearch =
      prevTextSearchRef.current?.length !== debouncedTextSearch.length;
    const isTextSearchDelete =
      prevTextSearchRef.current?.length > debouncedTextSearch.length;

    prevTextSearchRef.current = debouncedTextSearch;
    if (isTextSearch) {
      if (debouncedTextSearch || isTextSearchDelete) {
        if (isTextSearchDelete) {
          dispatch(getBlacklist(callRequest));
        } else if (
          debouncedTextSearch.length > 2 ||
          debouncedTextSearch.length == 0
        ) {
          dispatch(getBlacklist(callRequest));
        }
      }
    } else {
      dispatch(getBlacklist(callRequest));
    }
  }, [
    callRequest.From,
    callRequest.To,
    callRequest.PageIndex,
    callRequest.PageSize,
    callRequest.SortKeySelector,
    callRequest.SortOrder,
    debouncedTextSearch,
    debouncedReason,
  ]);

  useEffect(() => {
    if (Math.floor(total / callRequest.PageSize) < callRequest.PageIndex) {
      setCallRequest((prev) => ({
        ...prev,
        PageIndex: Math.floor(total / callRequest.PageSize),
      }));
    }
  }, [total]);

  const reload = useCallback(() => {
    dispatch(getBlacklist(callRequest));
  }, [dispatch, callRequest]);

  const { COLUMNS } = useBlacklistColumns({
    callRequest,
    disabled: status.responseLoading,
  });

  const divider = {
    height: 0,
    backgroundColor: 'transparent',
    margin: '0',
  };

  const {
    handleReasonChange,
    reasonValue,
    handleOpenReason,
    handleSearch,
    handleRangePicker,
    handleChange,
    handlePageSizeChange,
  } = useBlacklistHandlers({
    onIndex: setIndex,
    onCallRequest: setCallRequest,
    callRequest,
    onPageSize: setPageSize,
    total,
  });

  return {
    handleReasonChange,
    reasonValue,
    handleOpenReason,
    setCallRequest,
    callRequest,
    dateRange,
    isLoading: status.responseLoading,
    hasLoadError: status.responseError !== null,
    pageSize,
    total,
    handleChange,
    currentBlacklist,
    handleRangePicker,
    handleSearch,
    allBlacklist,
    COLUMNS,
    divider,
    handlePageSizeChange,
    onTableChange,
    reload,
  };
};
